<script>
  import { fade, scale } from "svelte/transition";
  import { backOut } from "svelte/easing";
  import { signup10Open, signup10Source } from "#lib/stores/signup10.js";
  import { userCountryStore } from "#lib/stores/countryStore.js";
  import { storePromoCode } from "#lib/utils/promo-code.js";
  import { captureEvent } from "#lib/utils/posthog.js";
  import { dialog } from "#lib/utils/dialog.js";
  import { browser } from "$app/environment";
  import { onMount, onDestroy, tick } from "svelte";
  import { campaign } from "#lib/stores/campaign.js";
  import { rewardLabel, withSymbol } from "#lib/campaign.js";
  import {
    HDYHAU,
    CONTEXT,
    VALUE,
    HINTS,
    orderOptions,
    missingPart,
    stepAnswer,
  } from "#lib/signup10/questions.js";

  // The questions, their values and the rules for Next live in
  // $lib/signup10/questions.js (hub ledger 2026-10-01-092: one template on all
  // three sites). This file is the frame.

  // "€10" / "$10", or the campaign's percentage while one runs
  // (lib/campaign.js). The reveal uses what the server says the minted code
  // gives, which only differs if the campaign ended mid-survey.
  $: hw = $campaign?.id === "halloween";
  $: reward = rewardLabel($campaign, $userCountryStore);
  let codePercent = null;
  $: shownReward = step === 5 && codePercent ? `${codePercent}%` : reward;

  // Randomize answer order once per page load (kills top-option bias), within
  // the platform group and within the rest, "Other" pinned last. In the
  // browser only, so SSR/hydration never disagree.
  let hdyhauOpts = HDYHAU.options;
  let contextOpts = CONTEXT.options;
  let valueOpts = VALUE.options;
  onMount(() => {
    hdyhauOpts = orderOptions(HDYHAU.options);
    contextOpts = orderOptions(CONTEXT.options);
    valueOpts = orderOptions(VALUE.options);
  });

  /** Two tiles per row: a follow-up opens under the row it belongs to. */
  const rows = (opts) => {
    const out = [];
    for (let i = 0; i < opts.length; i += 2) out.push(opts.slice(i, i + 2));
    return out;
  };

  // step 0 intro, 1 HDYHAU, 2 context, 3 value driver, 4 email, 5 reveal
  let step = 0;
  const TOTAL = 3; // question steps, for the progress dots

  let single = { hdyhau: "", use_context: "", value_driver: "" };
  let hdyhauPaid = "";
  let other = { hdyhau: "", use_context: "", value_driver: "" };

  $: hdyhauFollowUp = HDYHAU.options.find((o) => o.value === single.hdyhau)?.followUp;

  // Nothing auto-advances: Next waits until the step is complete. A tap on a
  // greyed Next outlines what is missing instead of doing nothing.
  $: answers = {
    hdyhau: single.hdyhau,
    hdyhauPaid,
    hdyhauOther: other.hdyhau,
    useContext: single.use_context,
    useContextOther: other.use_context,
    valueDriver: single.value_driver,
    valueDriverOther: other.value_driver,
  };
  $: missing = missingPart(step, answers);
  let showMissing = false;
  $: hint = showMissing && missing ? HINTS[missing] : "";

  // For signup_10_step: when the step was shown, and what was sent from it
  // last time, so a changed answer after Back is visible in the data.
  let stepShownAt = 0;
  let lastSent = {};
  let shownStep = -1;
  $: if (step !== shownStep) {
    shownStep = step;
    showMissing = false;
    stepShownAt = typeof performance !== "undefined" ? performance.now() : 0;
  }

  let email = "";
  let consent = false;
  let loading = false;
  let error = "";
  let code = "";
  // Set only when the code was minted for this visitor, so the reveal never
  // invents a deadline.
  let codeExpiresAt = "";
  let copied = false;

  // Confetti pieces for the reveal (values fixed once on load; browser Math.random OK)
  // Monochrome - black/greys to match the black & white modal.
  const confettiColors = ["#000000", "#333333", "#777777", "#bbbbbb", "#000000"];
  const confetti = Array.from({ length: 44 }, (_, i) => ({
    left: Math.round(Math.random() * 100),
    delay: (Math.random() * 0.6).toFixed(2),
    dur: (1.4 + Math.random() * 1.4).toFixed(2),
    size: (6 + Math.random() * 6).toFixed(1),
    color: confettiColors[i % confettiColors.length],
  }));

  /** "No thanks" on the intro: a decline, told apart from the corner close. */
  function decline() {
    captureEvent("signup_10_declined", { source: $signup10Source, campaign: $campaign?.id || null });
    close();
  }

  function close() {
    signup10Open.set(false);
    // reset a beat later so the closing transition isn't jarring
    setTimeout(() => {
      if (step !== 5) step = 0;
    }, 250);
  }

  // The free-text field opens below the tiles, often below the fold on a
  // phone: focus it so it scrolls into view and the keyboard comes up.
  let otherInput;
  const focusOther = async () => { await tick(); otherInput?.focus(); };

  function pick(key, value) {
    if (key === "hdyhau" && value !== single.hdyhau) hdyhauPaid = "";
    single[key] = value;
    if (value === "other") focusOther();
  }

  function next() {
    error = "";
    if (step >= 1 && step <= 3) {
      if (missing) {
        showMissing = true;
        return;
      }
      const a = stepAnswer(step, answers);
      const sent = JSON.stringify([a.answer, a.paid]);
      captureEvent("signup_10_step", {
        step,
        ...a,
        ms_on_step: Math.round(performance.now() - stepShownAt),
        changed: lastSent[step] !== undefined && lastSent[step] !== sent,
        source: $signup10Source,
      });
      lastSent[step] = sent;
    }
    if (step < 4) step += 1;
  }
  function back() {
    error = "";
    if (step > 0) step -= 1;
  }

  const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  function buildAnswers() {
    const otherFor = (key) => (single[key] === "other" ? other[key].trim() : "");
    return {
      hdyhau: single.hdyhau,
      hdyhau_paid: hdyhauFollowUp ? hdyhauPaid : "",
      hdyhau_other: otherFor("hdyhau"),
      use_context: single.use_context,
      use_context_other: otherFor("use_context"),
      value_driver: single.value_driver,
      value_driver_other: otherFor("value_driver"),
    };
  }

  async function submit() {
    error = "";
    if (!emailValid(email)) {
      error = "Please enter a valid email address.";
      return;
    }
    if (!consent) {
      error = "Please tick the box so we can send your reward.";
      return;
    }
    loading = true;
    try {
      const res = await fetch("/api/klaviyo/signup-10", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, answers: buildAnswers() }),
      });
      const data = await res.json();
      if (data.success && data.code) {
        code = data.code;
        codeExpiresAt = data.expiresAt || "";
        codePercent = data.percent || null;
        // Store the code so the cart/checkout auto-applies it (Cart.svelte +
        // ProductTemplate read this key).
        storePromoCode(code);
        step = 5;
        captureEvent("signup_10_completed", { source: $signup10Source });
      } else if (data.success) {
        // Subscribed, but Shopify would not mint a code even after the retry.
        // Stay on this step and say so: there is no shared code to fall back
        // to any more, and revealing an empty chip would claim a reward that
        // does not exist.
        error =
          "You're on the list, but we couldn't generate your code just now. Try again in a moment.";
        captureEvent("signup_10_no_code", { source: $signup10Source });
      } else {
        throw new Error(data.error || "Something went wrong.");
      }
    } catch (err) {
      error = err.message;
      captureEvent("signup_10_failed", { source: $signup10Source, error: err.message });
    } finally {
      loading = false;
    }
  }

  /** "25 September" in the visitor's locale, or "" if the date is unusable. */
  function expiryLabel(iso) {
    try {
      return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long" });
    } catch {
      return "";
    }
  }

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(code);
      copied = true;
      setTimeout(() => (copied = false), 2000);
    } catch {
      // Clipboard API unavailable or blocked (no permission / insecure context);
      // the code is visible on screen, so the user can copy it manually.
    }
  }

  // fire a "started" event the first time it opens, with the entry point that
  // opened it
  let started = false;
  $: if ($signup10Open && !started) {
    started = true;
    captureEvent("signup_10_started", { source: $signup10Source });
  }

  // Lock the page behind the popup so touch-scroll stays inside the modal.
  $: if (browser) document.body.style.overflow = $signup10Open ? "hidden" : "";
  onDestroy(() => {
    if (browser) document.body.style.overflow = "";
  });
</script>

{#if $signup10Open}
  <div class="overlay" transition:fade={{ duration: 180 }} on:click|self={close} role="presentation">
    <!-- See $lib/utils/dialog.js: Escape, focus trap and focus restore. The
         overlay stays role="presentation" - it is a backdrop, not the dialog. -->
    <div
      class="modal ph-no-deadclick"
      class:hw
      class:hw-intro={hw && step === 0}
      data-campaign={$campaign?.id || null}
      transition:scale={{ duration: 220, start: 0.94, easing: backOut }}
      role="dialog"
      aria-modal="true"
      aria-label="Get {reward} off"
      tabindex="-1"
      data-testid="signup10-dialog"
      use:dialog={{ onClose: close }}
    >
      <button class="close" type="button" aria-label="Close" data-testid="signup10-close" on:click={close}>&times;</button>

      {#if hw}
        <!-- Campaign art. A column beside every step on a wide screen, so the
             frame keeps its size between steps; a short band above the intro
             only on a phone, where the questions need the height. -->
        <div class="art" aria-hidden="true" data-testid="signup10-art">
          <img class="art-photo" src="/images/campaigns/halloween/survey-photo.webp" alt="" width="1122" height="1160" />
        </div>
      {/if}

      <div class="main">
      {#if step > 0 && step < 5}
        <div class="progress" aria-hidden="true">
          {#each Array(TOTAL) as _, i}
            <span class="dot" class:active={step >= i + 1}></span>
          {/each}
        </div>
      {/if}

      <div class="body" class:centred={(step === 0 && !hw) || step === 5}>
      {#if step === 0 && hw}
        <div class="pane hw-pane" data-testid="signup10-intro">
          <img class="hw-bats" src="/images/campaigns/halloween/bats.webp" alt="" width="420" height="198" />
          <p class="hw-tag">{withSymbol($campaign.surveyTagline, $userCountryStore)}</p>
          <h2 class="hw-h">Answer 3 quick questions &amp; get:</h2>
          <p class="hw-offer">
            <span class="hw-badge">{reward} off</span>
            {#if $campaign.freeShipping}<span class="hw-ship">+ Free shipping</span>{/if}
          </p>
          <div class="hw-bottom">
            <p class="sub hw-sub">Takes 20 seconds. Your code appears right at the end.</p>
            <button class="cta" on:click={next} data-testid="signup10-start">OK, let's go →</button>
            <button class="hw-no" type="button" on:click={decline} data-testid="signup10-no-thanks">No thanks</button>
          </div>
        </div>

      {:else if step === 0}
        <div class="pane">
          <p class="eyebrow">GLORB</p>
          <h2>Answer 3 quick questions &amp; get <span class="hl">{reward} off</span></h2>
          <p class="sub">Takes 20 seconds. Your code appears right at the end.</p>
          <button class="cta" on:click={next}>OK, let's go →</button>
        </div>

      {:else if step >= 1 && step <= 3}
        {@const q = step === 1 ? HDYHAU : step === 2 ? CONTEXT : VALUE}
        {@const opts = step === 1 ? hdyhauOpts : step === 2 ? contextOpts : valueOpts}
        <div class="pane">
          <p class="eyebrow">Get {reward} off</p>
          <h2>{q.title}</h2>
          <div class="options" class:missing={hint && missing === "pick"}>
            {#each rows(opts) as row}
              {#each row as o (o.value)}
                <button
                  class="opt"
                  class:sel={single[q.key] === o.value}
                  aria-pressed={single[q.key] === o.value}
                  on:click={() => pick(q.key, o.value)}
                  data-testid="signup10-opt-{q.key}-{o.value}"
                >{o.label}</button>
              {/each}
              {#if step === 1 && hdyhauFollowUp && row.some((o) => o.value === single.hdyhau)}
                <!-- A tap on Next before this is answered outlines the panel and
                     says nothing: its own question already reads as the
                     instruction (Simon, 2026-10-01). -->
                <div
                  class="subq"
                  class:missing={hint && missing === "followup"}
                  data-testid="signup10-hdyhau-followup"
                >
                  <p class="subq-title">{HDYHAU.followUpTitle}</p>
                  <div class="pills">
                    {#each hdyhauFollowUp as f (f.value)}
                      <button
                        class="pill"
                        class:sel={hdyhauPaid === f.value}
                        aria-pressed={hdyhauPaid === f.value}
                        on:click={() => (hdyhauPaid = f.value)}
                        data-testid="signup10-paid-{f.value}"
                      >{f.label}</button>
                    {/each}
                  </div>
                </div>
              {/if}
            {/each}
          </div>
          {#if single[q.key] === "other"}
            <input
              class="txt"
              class:missing={hint && missing === "other"}
              placeholder={step === 1 ? "Tell us where…" : "Tell us more…"}
              aria-label="{q.title} Other"
              bind:value={other[q.key]}
              bind:this={otherInput}
              data-testid="signup10-other-input"
            />
          {/if}
          {#if hint && missing !== "followup"}
            <p class="hint" role="status" data-testid="signup10-hint">{hint}</p>
          {/if}
        </div>

      {:else if step === 4}
        <div class="pane">
          <h2>Enter your email to receive your code</h2>
          <p class="sub">Your {reward} code appears on the next screen, and we'll send you news and offers from GLORB. No spam, unsubscribe anytime.</p>
          <form on:submit|preventDefault={submit}>
            <input class="txt" type="email" placeholder="you@example.com" aria-label="Email address" autocomplete="email" bind:value={email} required />
            <label class="consent">
              <input type="checkbox" bind:checked={consent} />
              <span>I agree to receive emails with offers, codes &amp; news from GLORB. See our <a href="/policies/privacy-policy/" target="_blank" rel="noopener">Privacy Policy</a>.</span>
            </label>
            {#if error}<p class="error" data-testid="signup10-error">{error}</p>{/if}
            <button class="cta" type="submit" disabled={loading}>{loading ? "One sec…" : `Reveal my ${reward} code`}</button>
          </form>
        </div>

      {:else}
        <div class="pane reveal">
          <div class="confetti" aria-hidden="true">
            {#each confetti as c}
              <span class="piece" style="left:{c.left}%; width:{c.size}px; height:{c.size}px; background:{c.color}; animation-delay:{c.delay}s; animation-duration:{c.dur}s;"></span>
            {/each}
          </div>
          <p class="eyebrow">You're in!</p>
          <h2>Here's your <span class="hl">{shownReward} off</span></h2>
          <button class="code-chip" data-testid="signup10-code-chip" on:click={copyCode} title="Click to copy" in:scale={{ duration: 300, start: 0.6, easing: backOut }}>
            <span class="code">{code}</span>
            <span class="copy">{copied ? "Copied!" : "Copy"}</span>
          </button>
          <p class="sub">Copy the code and use it at checkout for {shownReward} off your order.</p>
          {#if codeExpiresAt}
            <!-- The code is yours alone and good once, so it has to carry its
                 deadline: a week later it stops working and nothing else on
                 screen would have said so. Another one is one survey away. -->
            <p class="sub expiry" data-testid="signup10-code-expiry">
              Yours alone, good for one order &ndash; valid until {expiryLabel(codeExpiresAt)}.
              {#if codePercent && $campaign?.excludedQty?.includes(4)}Not valid on the 4-pack, which is already 1 GLORB free.{/if}
            </p>
          {/if}
          <a class="cta" href="/products/the-glorb/" on:click={close}>Shop GLORB →</a>
        </div>
      {/if}
      </div>

      {#if step >= 1 && step <= 4}
        <!-- One footer for every question: Back left, Next right, always on
             screen however long the step is. Next is aria-disabled, not
             disabled, so a tap still lands and can say what is missing. -->
        <div class="foot">
          <button class="back" type="button" on:click={back} data-testid="signup10-back">← Back</button>
          {#if step <= 3}
            <button
              class="cta next"
              type="button"
              class:is-disabled={!!missing}
              aria-disabled={missing ? "true" : "false"}
              on:click={next}
              data-testid="signup10-next"
            >Next →</button>
          {/if}
        </div>
      {/if}
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  // One frame for the survey on all three sites (hub ledger 2026-10-01-092):
  // the brands change only colours, radius, font and heading weight. A fixed
  // head (close, dots), a body that scrolls, and a footer with Back and Next
  // that never leaves the screen.
  .overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    z-index: 2000;
  }
  .modal {
    position: relative;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: 100%;
    max-width: 480px;
    max-height: min(calc(100vh - 32px), 760px);
    max-height: min(calc(100dvh - 32px), 760px);
    overflow: hidden;
    background: #ffffff;
    color: #000;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 10px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  }
  .close {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 2;
    width: 36px;
    height: 36px;
    background: none;
    border: none;
    color: #000;
    font-size: 1.6rem;
    line-height: 1;
    cursor: pointer;
    opacity: 0.5;
  }
  .close:hover { opacity: 1; }

  .progress { display: flex; gap: 6px; justify-content: center; align-items: center; height: 60px; padding: 0 56px; flex: 0 0 auto; }
  .dot { width: 26px; height: 4px; border-radius: 2px; background: rgba(0, 0, 0, 0.12); transition: background 0.2s; }
  .dot.active { background: #000; }

  .body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 20px 28px;
    display: flex;
    flex-direction: column;
  }
  .body.centred { justify-content: center; padding-top: 48px; padding-bottom: 32px; }

  .pane { display: flex; flex-direction: column; }
  .eyebrow { color: #000; text-transform: uppercase; letter-spacing: 0.08em; font-size: 12px; margin: 0 0 8px; }
  h2 { color: #000; font-size: 21px; font-weight: 400; line-height: 1.25; margin: 0 0 16px; }
  .hl { color: #000; text-decoration: underline; text-decoration-thickness: 2px; text-underline-offset: 2px; }
  .sub { color: rgba(0, 0, 0, 0.6); font-size: 0.92rem; margin: -6px 0 16px; }

  .options {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 8px;
    border-radius: 10px;
    outline: 1px solid transparent;
    outline-offset: 4px;
  }
  .options.missing { outline-color: #000; }
  .opt {
    min-height: 48px;
    text-align: left; padding: 10px 14px; border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.18); background: #fff; color: #000;
    font-size: 15px; line-height: 1.25; cursor: pointer; transition: border-color 0.15s, background 0.15s;
    display: flex; align-items: center; gap: 10px;
  }
  .opt:hover { border-color: #000; }
  .opt.sel { border-color: #000; border-width: 2px; padding: 9px 13px; background: rgba(0, 0, 0, 0.05); }

  // A follow-up spans the whole row under the tile it belongs to.
  .subq {
    grid-column: 1 / -1;
    padding: 12px 14px;
    border: 2px solid transparent;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.04);
  }
  .subq.missing { border-color: #000; }
  .subq-title { margin: 0 0 8px; color: rgba(0, 0, 0, 0.6); font-size: 13px; }

  .pills { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
  .pill {
    min-height: 48px; padding: 6px 8px; border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.18); background: #fff; color: #000;
    font-size: 14px; line-height: 1.2; cursor: pointer;
  }
  .pill:hover { border-color: #000; }
  .pill.sel { background: #000; border-color: #000; color: #fff; }

  .hint { color: #000; font-size: 13px; margin: 10px 0 0; }

  .txt {
    padding: 12px 14px; border-radius: 10px; border: 1px solid rgba(0, 0, 0, 0.2);
    background: #fff; color: #000; font-size: 16px; margin: 8px 0;
  }
  .txt:focus { outline: none; border-color: #000; }
  .txt.missing { border-color: #000; border-width: 2px; }

  form { display: flex; flex-direction: column; gap: 0.9rem; }
  .consent { display: flex; gap: 0.55rem; align-items: flex-start; font-size: 0.82rem; color: rgba(0, 0, 0, 0.6); }
  .consent a { color: #000; }

  .cta {
    margin-top: 0.5rem; padding: 0.85rem 1rem; border-radius: 10px; border: none;
    background: #000; color: #fff; font-weight: 500; font-size: 1rem;
    cursor: pointer; text-align: center; text-decoration: none; display: block;
  }
  .cta:hover { background: #1a1a1a; }
  .cta[disabled] { opacity: 0.6; cursor: default; }

  .foot {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 28px calc(12px + env(safe-area-inset-bottom));
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 -8px 16px rgba(0, 0, 0, 0.06);
    background: #fff;
  }
  .foot .cta { margin: 0 0 0 auto; min-width: 110px; min-height: 48px; }
  .foot .cta.is-disabled { background: #b5b5b5; cursor: default; }

  .back { min-height: 44px; padding: 0 4px; background: none; border: none; color: rgba(0, 0, 0, 0.55); cursor: pointer; font-size: 0.92rem; }
  .back:hover { color: #000; }
  .error { color: #c0392b; font-size: 0.9rem; margin: 0; }

  .reveal { text-align: center; align-items: center; }
  .confetti { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 0; }
  .piece {
    position: absolute; top: -18px; border-radius: 1px; opacity: 0;
    animation-name: fall; animation-timing-function: linear;
    animation-iteration-count: 1; animation-fill-mode: forwards;
  }
  @keyframes fall {
    0% { transform: translateY(-20px) rotate(0); opacity: 0; }
    12% { opacity: 1; }
    100% { transform: translateY(560px) rotate(700deg); opacity: 1; }
  }
  .reveal > :not(.confetti) { position: relative; z-index: 1; }
  .code-chip {
    display: inline-flex; align-items: center; gap: 0.75rem; margin: 0.75rem auto 1rem;
    padding: 0.8rem 1.1rem; border: 2px dashed #000; border-radius: 10px;
    background: rgba(0, 0, 0, 0.04); cursor: pointer;
  }
  .code-chip .code { font-size: 1.9rem; font-weight: 500; letter-spacing: 0.08em; color: #000; }
  .code-chip .copy { font-size: 0.78rem; color: rgba(0, 0, 0, 0.55); text-transform: uppercase; letter-spacing: 0.06em; }
  .sub.expiry { opacity: 0.75; }
  .reveal .cta { max-width: 220px; margin: 0.5rem auto 0; }

  /* Match the site font everywhere (buttons/inputs don't inherit it by default) */
  .modal,
  .modal * { font-family: "Montreal regular", system-ui, -apple-system, sans-serif; }

  // Phones: a fixed height, so the frame does not jump between steps and
  // Back/Next sit in the same place on every one.
  @media (max-width: 480px) {
    .modal { height: min(calc(100vh - 32px), 760px); height: min(calc(100dvh - 32px), 760px); }
    .progress { height: 52px; }
    .body { padding: 4px 20px 16px; }
    .body.centred { padding-top: 48px; }
    .foot { padding-left: 20px; padding-right: 20px; }
    .close { top: 8px; right: 8px; }
    .opt { padding: 10px 12px; }
    .opt.sel { padding: 9px 11px; }
    .code-chip .code { font-size: 1.6rem; }
  }

  // The head, body and footer live in .main, so a campaign can put art beside
  // them without changing how they scroll.
  .main {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
  }

  /* ---- Halloween (lib/campaign.js) ----------------------------------- */
  // GLORB keeps its light card: grey survey side, black type and buttons,
  // orange offer badge. SOLDR's version of the same layout is dark.
  $hw-bg: #e6e6e6;
  $hw-orange: #f07a2c;

  .modal.hw { background: $hw-bg; border-radius: 0; }
  .modal.hw .foot { background: $hw-bg; }
  .modal.hw .close {
    background: #000;
    color: #fff;
    opacity: 1;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    font-size: 1.3rem;
  }

  .art { position: relative; overflow: hidden; background: #1b1714; }
  .art-photo { display: block; width: 100%; height: 100%; object-fit: cover; }

  // The photo carries its own lettering ("Halloween Special" across the top,
  // "15% off" across the bottom, edge to edge), so the column takes the photo's
  // shape instead of cropping it: at 580px tall it is 561px wide, and the
  // survey keeps the 435px it had; the photo is fitted, never cut.
  // Side by side only from 1040px: below that the window cannot give the
  // photo its 561px and the survey its 435px, so tablets get the stacked
  // phone layout.
  @media (min-width: 1040px) {
    .modal.hw {
      flex-direction: row;
      max-width: 996px;
      height: min(calc(100vh - 32px), 580px);
      height: min(calc(100dvh - 32px), 580px);
    }
    .modal.hw .art { flex: 0 0 auto; height: 100%; aspect-ratio: 1122 / 1160; max-width: 57%; }
    .modal.hw .art-photo { object-fit: contain; }
  }

  .hw-pane { flex: 1 1 auto; position: relative; }
  .modal.hw-intro .body { padding: 56px 28px 20px; }
  .hw-bats {
    position: absolute;
    top: -28px;
    right: 24px;
    width: 140px;
    height: auto;
    pointer-events: none;
  }
  .hw-tag,
  .hw-h,
  .hw-badge,
  .hw-ship { font-family: "Montreal bold", system-ui, -apple-system, sans-serif; color: #000; }
  .hw-tag { font-size: 15px; margin: 96px 0 18px; }
  .hw-h {
    font-size: 30px;
    line-height: 1.1;
    text-transform: uppercase;
    margin: 0 0 22px;
  }
  .hw-offer { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin: 0; }
  .hw-badge,
  .hw-ship { font-size: 26px; line-height: 1; text-transform: uppercase; }
  .hw-badge {
    color: #fff;
    background: $hw-orange;
    padding: 6px 10px 8px;
    text-decoration: underline;
    text-decoration-thickness: 3px;
    text-underline-offset: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  }
  .hw-bottom { display: flex; flex-direction: column; margin-top: auto; padding-top: 24px; }
  .hw-sub { text-align: center; font-size: 13px; margin: 0 0 8px; }
  .hw-bottom .cta { margin-top: 0; min-height: 52px; font-size: 1.05rem; }
  .hw-no {
    align-self: center;
    min-height: 44px;
    padding: 0 12px;
    background: none;
    border: 0;
    color: rgba(0, 0, 0, 0.55);
    font-size: 14px;
    cursor: pointer;
  }
  .hw-no:hover { color: #000; }

  // Phones and narrow tablets: no art on the questions; the intro shows the
  // whole photo above it. A band cut "15% off" off the bottom of the photo
  // (Simon, 2026-10-07), and the photo already says "Halloween Special" and
  // "15% off", so the intro drops what repeats it - the tagline, the bats and
  // the "Takes 20 seconds" line - to keep the button on the first screen.
  // A short screen caps the photo's height and fits it, never cuts it.
  @media (max-width: 1039px) {
    .modal.hw { max-width: 480px; }
    .modal.hw .art { display: none; }
    .modal.hw-intro .art {
      display: block;
      flex: 0 0 auto;
      width: 100%;
      aspect-ratio: 1122 / 1160;
      max-height: 48vh;
      max-height: 48svh;
      background: #000;
    }
    .modal.hw-intro .art-photo { object-fit: contain; }
    .modal.hw-intro .body { padding: 18px 20px 12px; }
    .hw-bats, .hw-tag, .hw-sub { display: none; }
    .hw-h { font-size: 22px; margin-bottom: 14px; }
    .hw-badge, .hw-ship { font-size: 20px; }
    .hw-bottom { padding-top: 16px; }
  }
  @media (max-width: 1039px) and (max-height: 720px), (max-width: 359px) {
    .hw-h { font-size: 20px; margin-bottom: 12px; }
    .hw-badge, .hw-ship { font-size: 17px; }
  }
  // The intro is as tall as its content on a phone; the questions take the
  // fixed frame back from step 1.
  @media (max-width: 480px) {
    .modal.hw-intro { height: auto; }
    // Small and tucked into the corner: the lettering runs edge to edge.
    .modal.hw-intro .close { top: 6px; right: 6px; width: 32px; height: 32px; }
  }
</style>
